import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import DOMPurify from 'dompurify';

import { getCardPreviewTemplateRequest } from 'features/my-gift-cards/api';
import cardQRCodeImage from 'assets/images/card-qr-code.png';
import { CartPreviewTemplateParams, TemplateParamsKey } from 'features/my-gift-cards/ts/CartPreviewTemplateParams';
import { toTemplateVariable } from 'features/my-gift-cards/utils/toTemplateVariable';
import { mapTemplateParam } from 'features/my-gift-cards/utils/mapTemplateParam';

export interface CardPreviewProps {
  show: boolean;
  templateParams: CartPreviewTemplateParams;
}

const fetchCardPreview = async () => {
  const { data } = await getCardPreviewTemplateRequest();
  return data;
};

export const CardPreview: React.FC<CardPreviewProps> = ({ show, templateParams }) => {
  const ref = React.useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState('1720px');
  const { data } = useQuery('card-template', fetchCardPreview, { staleTime: Infinity });

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.height) {
        setIframeHeight(event.data.height + 'px');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const filledTemplate = React.useMemo(() => {
    if (!show) return '';

    const template = Object.keys(templateParams)
      .reduce(
        (result, key) =>
          result?.replaceAll(
            toTemplateVariable(key as TemplateParamsKey),
            mapTemplateParam(templateParams[key as TemplateParamsKey])
          ),
        data
      )
      ?.replace(`{{ amount | floatformat }}`, '100')
      ?.replace('href="{{ gift_card_url }}"', 'style="pointer-events: none;"')
      ?.replace('{{ giftcard_number }}', '2434501547309110')
      ?.replace('{{ order_id }}', 'OD2152D985FC56')
      ?.replace('{{ currency }}', 'AED')
      ?.replace('{% if barcode_url %}', '')
      ?.replace(`{% endif %}`, '')
      ?.replace('{{ barcode_url }}', cardQRCodeImage);

    if (template) {
      const purified = DOMPurify.sanitize(template, {
        FORCE_BODY: true,
        ALLOW_UNKNOWN_PROTOCOLS: true,
      });

      // This is necessary to set correct height for the iframe
      const script =
        "<script type='application/javascript'>window.onload = () => {\n" +
        '  window.parent.postMessage(\n' +
        '    { height: document.body.scrollHeight },\n' +
        '    "*"\n' +
        '  );\n' +
        '  console.log(document.body.scrollHeight) ' +
        '};</script>';

      return purified.toString() + script;
    }
    return;
  }, [data, templateParams, show]);

  return (
    <iframe
      id={'frame-preview'}
      srcDoc={filledTemplate ? filledTemplate : undefined}
      ref={ref}
      style={
        show
          ? {
              width: '100%',
              height: iframeHeight,
              border: 'none',
              overflow: 'hidden',
            }
          : { display: 'none' }
      }
      frameBorder="0"
    />
  );
};
